const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const asArray = value => (Array.isArray(value) ? value : []);

const asNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const compact = values => values.filter(Boolean);

export const CONDOMINIUM_STATUS_LABEL_KEYS = Object.freeze({
  good: 'CONDOMINIUMS.STATUS.GOOD',
  review: 'CONDOMINIUMS.STATUS.REVIEW',
  blocked: 'CONDOMINIUMS.STATUS.BLOCKED',
  ready: 'CONDOMINIUMS.STATUS.READY',
  published: 'CONDOMINIUMS.STATUS.PUBLISHED',
  draft: 'CONDOMINIUMS.STATUS.DRAFT',
});

export const statusLabel = (status, translate = key => key) => {
  const key = CONDOMINIUM_STATUS_LABEL_KEYS[status];
  if (key) return translate(key);
  return status
    ? String(status)
    : translate(CONDOMINIUM_STATUS_LABEL_KEYS.review);
};

export const normalizeCondominium = item => {
  const quality = item?.quality || item?.qualidade || {};
  const publicability = item?.publicability || item?.publicabilidade || {};
  const seo = item?.seo || {};
  const aliases = asArray(item?.aliases || item?.alias_names);
  const blockers = asArray(publicability.blockers);
  const warnings = asArray(publicability.warnings);
  const conflicts = asArray(item?.conflicts || item?.conflitos);
  const listingsAvailable = asNumber(
    firstPresent(publicability.listings_available, item?.listings_available)
  );
  const score = asNumber(firstPresent(quality.score, item?.quality_score));
  const qualityStatus = firstPresent(quality.status, item?.quality_status);
  const publicState = firstPresent(
    publicability.state,
    item?.publication_state,
    item?.status_publicacao
  );

  return {
    id: item?.id,
    nome: item?.nome || '',
    slug: item?.slug || '',
    cidade: item?.cidade || '',
    bairro: item?.bairro || '',
    uf: item?.uf || '',
    endereco: item?.endereco || '',
    numero: item?.numero || '',
    cep: item?.cep || '',
    addressLabel: compact([
      item?.endereco,
      item?.numero,
      item?.bairro,
      item?.cidade,
      item?.uf,
    ]).join(', '),
    thumbnail: item?.thumbnail || '',
    publicUrl: item?.public_url || item?.publicUrl || '',
    listingsAvailable,
    score,
    qualityStatus: qualityStatus || (score >= 80 ? 'good' : 'review'),
    qualityChecks: asArray(quality.checks),
    publicable: Boolean(
      firstPresent(publicability.publicable, blockers.length === 0)
    ),
    publicState: publicState || (blockers.length ? 'blocked' : 'ready'),
    blockers,
    warnings,
    conflicts,
    aliases,
    seo: {
      title: seo.title || '',
      description: seo.description || '',
      canonicalPath: seo.canonical_path || seo.canonicalPath || '',
      indexable: firstPresent(seo.indexable, true),
    },
    permissions: item?.permissions || {},
  };
};

export const normalizeCondominiumsResponse = response => {
  const body = response?.data || response || {};
  const rows = asArray(body.data || body.payload);

  return {
    meta: body.meta || {},
    condominiums: rows.map(normalizeCondominium),
  };
};

export const normalizeListing = item => ({
  id: item?.id,
  titulo: item?.titulo || item?.title || '',
  status: item?.status || '',
  finalidade: item?.finalidade || '',
  tipo: item?.tipo || '',
  condominiumId: item?.condominium_id || item?.condominiumId || null,
  condominiumName: item?.condominium_name || item?.condominiumName || '',
  endereco: item?.endereco || '',
  bairro: item?.bairro || '',
  cidade: item?.cidade || '',
  uf: item?.uf || '',
  thumbnail: item?.thumbnail || '',
  valorVenda: item?.valor_venda || item?.valorVenda || null,
  valorAluguel: item?.valor_aluguel || item?.valorAluguel || null,
});

export const normalizeListingsResponse = response => {
  const body = response?.data || response || {};
  const rows = asArray(body.data || body.payload);

  return {
    meta: body.meta || {},
    listings: rows.map(normalizeListing),
  };
};

export const listingLinkState = (listing, condominiumId) => {
  if (!listing?.condominiumId) return 'available';
  if (String(listing.condominiumId) === String(condominiumId)) return 'linked';
  return 'linked_elsewhere';
};

export const formatMoney = value => {
  if (value === null || value === undefined || value === '') return '';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value));
};
